import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { faqAPI } from '../services/api';

export default function FAQSection({
  title = 'Frequently Asked Questions',
  subtitle = 'Find quick answers to common questions about Calamus Education.',
  sx = {},
}) {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        setLoading(true);
        const response = await faqAPI.get();
        if (response?.success && Array.isArray(response.data)) {
          setFaqs(response.data);
        } else {
          setFaqs([]);
        }
      } catch (err) {
        console.error('Failed to fetch FAQs:', err);
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };

    loadFaqs();
  }, []);

  const sortedFaqs = useMemo(() => {
    return [...faqs].sort((a, b) => {
      const sortOrderDiff = Number(a?.sortOrder ?? 0) - Number(b?.sortOrder ?? 0);
      if (sortOrderDiff !== 0) return sortOrderDiff;
      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });
  }, [faqs]);

  if (!loading && sortedFaqs.length === 0) {
    return null;
  }

  return (
    <Box sx={sx}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
          {subtitle}
        </Typography>
      </Box>

      {loading ? (
        <Box>
          {[1, 2, 3].map((item) => (
            <Paper
              key={item}
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 2.25 }}>
                <Skeleton variant="text" width="70%" height={28} />
                <Skeleton variant="text" width="92%" height={20} />
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Box>
          {sortedFaqs.map((faq, index) => (
            <Accordion
              key={faq.id || index}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: '16px !important',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 2.25,
                  py: 0.25,
                  '& .MuiAccordionSummary-content': {
                    my: 1.25,
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.25, pb: 2.25, pt: 0 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
